import { useCallback, useState, type ChangeEvent } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

// import custom components
import { DetailsDialog } from "../../components/Dialog";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import Logs from "../../assets/images/logs.png";

// importing css and svg  or images
import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./user.module.css";

// importing services
import { useGetUserRolesQuery } from "../../services/api";
import { colors } from "../../theme/colors";

/** Action identifiers for user role rows. */
type UserRoleAction = "view" | "edit" | "logs";

/**
 * Renders a status badge for list rows.
 *
 * Args:
 *   isActive: Status flag from the API.
 *
 * Returns:
 *   JSX.Element: Status badge component.
 */
const renderStatusBadge = (isActive?: boolean) => (
  <StatusBadge isActive={isActive ?? false} />
);

const getRoleActionItems = (isActive: boolean): ActionItem<UserRoleAction>[] => [
  {
    label: "View",
    value: "view",
    icon: <EyeIcon height={24} width={24} />,
  },
  ...(isActive
    ? ([
      {
        label: "Edit",
        value: "edit",
        icon: <EditIcon height={24} width={24} />,
      },
    ] as ActionItem<UserRoleAction>[])
    : []),
  {
    label: "Logs",
    value: "logs",
    icon: <Box component="img" src={Logs} width={24} />,
  },
];

/**
 * Renders the user role list page with search and action menu navigation.
 *
 * Returns:
 *   JSX.Element: User role list view with search input and action menu.
 */
const UserRoles = () => {
  const navigate = useNavigate();

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    name: "",
    is_detailed: true,
  });

  const { data: roleData, isFetching } = useGetUserRolesQuery(queryParam);

  const [selectedUserRole, setSelectedUserRole] = useState<UserRole | null>(
    null
  );
  const [logModalVisible, setLogModalVisible] = useState(false);

  const fields = [
    { id: "name", label: "User Role", flex: 2, sortable: true },
    {
      id: "is_active",
      label: "Status",
      type: "badge",
      flex: 2,
      sortable: true,
      render: (item: UserRole) => renderStatusBadge(item.is_active),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      render: (item: UserRole) => (
        <CustomActionMenu<UserRole, UserRoleAction>
          item={item}
          isActive={item.is_active}
          handleChange={handleMenu}
          actions={getRoleActionItems(item.is_active ?? false)}
        />
      ),
    },
  ];

  const handleMenu = (value: UserRoleAction, item: UserRole) => {
    setSelectedUserRole(item);
    // Route based on action selection to reuse a single action menu for all rows.
    if (value === "view") {
      navigate(`/user/roles/details/${item?.id}`);
    } else if (value === "edit") {
      navigate(`/user/roles/edit/${item?.id}`);
    } else if (value === "logs") {
      setLogModalVisible(true);
    }
  };

  // for searching in the query
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        name: e.target.value,
      }));
    },
    [setQueryParam]
  );

  // Render the User role component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.name}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
      </Box>
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<UserRole>
          fields={fields}
          paginationRequired={true}
          data={roleData?.items || []}
          loading={isFetching}
          onDoubleClick={(userRole: UserRole) => {
            navigate(`/user/roles/details/${userRole?.id}`);
          }}
          defaultOrderBy="name"
          searchTerm={queryParam.name}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, name: "" }))}
        />
      </Box>
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedUserRole}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};
export default UserRoles;
